import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { Button, Card, CardBody, CardHeader, Table } from "reactstrap";
import { ClipLoader } from "react-spinners";
import api from "services/api";
import {
  AUTOPILOT_COLORS,
  autopilotBodyStyle,
  autopilotCardStyle,
  autopilotDescriptionStyle,
  autopilotEyebrowStyle,
  autopilotHeaderStyle,
  autopilotTitleStyle,
} from "../../../../components/autopilot/styles";
import "../PisoAntt/styles.css";
import "./styles.css";

const fmtKm = (v) =>
  v === null || v === undefined
    ? "-"
    : Number(v).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      });

const fmtBRL = (v) =>
  v === null || v === undefined
    ? "-"
    : Number(v).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
      });

const fmtData = (v) => (v ? moment(String(v).slice(0, 10)).format("DD/MM/YYYY") : "-");

const TabelaAntt = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/indicadores/antt/tabela");
      setData(res.data);
    } catch (requestError) {
      setError(
        (requestError.response &&
          requestError.response.data &&
          requestError.response.data.error) ||
          "Erro ao carregar a tabela ANTT."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Pivot: linhas -> { eixos[], grupos[{ tipoOperacao, tipoCarga, porEixo:{} }] }
  const linhas = (data && data.linhas) || [];
  const eixos = Array.from(new Set(linhas.map((l) => l.qtdEixos))).sort(
    (a, b) => a - b
  );
  const gruposMap = {};
  linhas.forEach((l) => {
    const key = `${l.tipoOperacao}||${l.tipoCarga}`;
    if (!gruposMap[key]) {
      gruposMap[key] = {
        tipoOperacao: l.tipoOperacao,
        tipoCarga: l.tipoCarga,
        porEixo: {},
      };
    }
    gruposMap[key].porEixo[l.qtdEixos] = l;
  });
  const grupos = Object.keys(gruposMap).map((k) => gruposMap[k]);

  return (
    <div className="content">
      <Card
        className="card-everlog-shell"
        style={{ ...autopilotCardStyle, background: "var(--everlog-card-hero-bg)" }}
      >
        <CardHeader
          style={{
            ...autopilotHeaderStyle,
            borderBottom: "none",
            padding: "1.65rem 1.65rem 1.45rem",
          }}
        >
          <div className="antt-drill-head">
            <div>
              <div style={{ ...autopilotEyebrowStyle, marginBottom: "0.6rem" }}>
                Conformidade Regulatória
              </div>
              <h2
                style={{
                  ...autopilotTitleStyle,
                  margin: "0 0 0.55rem",
                  fontSize: "2rem",
                  fontWeight: 800,
                  lineHeight: 1.15,
                }}
              >
                Tabela ANTT, Piso Mínimo de Frete
              </h2>
              <p
                style={{
                  ...autopilotDescriptionStyle,
                  maxWidth: 820,
                  marginBottom: 0,
                  fontSize: "0.98rem",
                  lineHeight: 1.65,
                }}
              >
                {data
                  ? `Resolução ${data.versao} · vigente desde ${fmtData(
                      data.dataInicioVigencia
                    )}. CCD = coeficiente de deslocamento (R$/km); CD = carga e descarga (R$).`
                  : "Coeficientes do piso mínimo de frete por tipo de carga e número de eixos."}
              </p>
            </div>
            {data && data.urlPdf ? (
              <Button
                color="info"
                size="sm"
                tag="a"
                href={data.urlPdf}
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="tim-icons icon-paper" /> Abrir PDF
              </Button>
            ) : null}
          </div>
        </CardHeader>
      </Card>

      {loading ? (
        <div className="antt-loading">
          <ClipLoader color={AUTOPILOT_COLORS.title} size={32} />
        </div>
      ) : error ? (
        <Card
          className="card-everlog-shell"
          style={{ ...autopilotCardStyle, marginTop: "1.25rem", background: "var(--everlog-card-hero-bg)" }}
        >
          <CardBody style={autopilotBodyStyle}>
            <div className="antt-state antt-state--error">{error}</div>
          </CardBody>
        </Card>
      ) : (
        <Card
          className="card-everlog-shell"
          style={{ ...autopilotCardStyle, marginTop: "1.25rem", background: "var(--everlog-card-hero-bg)" }}
        >
          <CardBody style={autopilotBodyStyle}>
            <div className="antt-table-wrap">
              <Table className="antt-table" responsive>
                <thead>
                  <tr>
                    <th>Tabela</th>
                    <th>Tipo de carga</th>
                    {eixos.map((e) => (
                      <th key={e} className="antt-num">
                        {e} eixos
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {grupos.map((g) => (
                    <tr key={`${g.tipoOperacao}||${g.tipoCarga}`}>
                      <td>{g.tipoOperacao}</td>
                      <td>{g.tipoCarga}</td>
                      {eixos.map((e) => {
                        const linha = g.porEixo[e];
                        return (
                          <td key={e} className="antt-num">
                            {linha ? (
                              <>
                                <div>{fmtKm(linha.valorPorKm)}</div>
                                <div className="antt-cell-sub">
                                  CD {fmtBRL(linha.valorCargaDescarga)}
                                </div>
                              </>
                            ) : (
                              "-"
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            {!data || !data.urlPdf ? (
              <p className="antt-footnote">
                PDF da resolução ainda não configurado (campo UrlPdf da TabelaANTT).
              </p>
            ) : null}
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default TabelaAntt;
